import { useParams, Link, Navigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import ContactBlock from "@/components/ContactBlock";
import ArticleCard from "@/components/ArticleCard";
import { PhoneCTA, WhatsAppCTA } from "@/components/ContactButtons";
import { ARTICLES } from "@/data/content";
import { ArrowLeft, ArrowRight, ChevronRight } from "lucide-react";

const Article = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <Navigate to="/conseils" replace />;

  const related = (article.related || [])
    .map((s) => ARTICLES.find((a) => a.slug === s))
    .filter(Boolean);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      inLanguage: "fr-BE",
      datePublished: article.datePublished,
      author: { "@type": "Organization", name: "AVELIS" },
      publisher: { "@type": "Organization", name: "AVELIS" },
      articleSection: article.category,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil" },
        { "@type": "ListItem", position: 2, name: "Conseils" },
        { "@type": "ListItem", position: 3, name: article.title },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${article.title} | AVELIS`}
        description={article.excerpt}
        jsonLd={jsonLd}
      />

      <article data-testid="article-page" className="pt-[130px] sm:pt-[160px] pb-16 sm:pb-24 bg-[#f6f3ec]">
        <div className="container-avelis max-w-3xl">
          <Reveal>
            <nav aria-label="Fil d'Ariane" data-testid="article-breadcrumb" className="flex items-center gap-1.5 text-xs font-semibold text-[#4b585e] flex-wrap">
              <Link to="/" className="hover:text-[#176b70] transition-colors">Accueil</Link>
              <ChevronRight className="h-3.5 w-3.5 text-[#b5c4b1]" aria-hidden="true" />
              <Link to="/conseils" className="hover:text-[#176b70] transition-colors">Conseils</Link>
              <ChevronRight className="h-3.5 w-3.5 text-[#b5c4b1]" aria-hidden="true" />
              <span className="text-[#0d2731] truncate max-w-[220px] sm:max-w-none">{article.title}</span>
            </nav>

            <div className="mt-8 flex items-center gap-2.5 flex-wrap">
              <span className="rounded-full bg-[#176b70]/10 text-[#176b70] px-3 py-1 text-[11px] font-bold">{article.category}</span>
              <span className="text-[11px] font-semibold text-[#4b585e]/70">{article.date} • Lecture {article.readTime}</span>
            </div>

            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight leading-[1.12] text-[#0d2731]">
              {article.title}
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-[#0d2731]/85 leading-relaxed font-medium border-l-2 border-[#b5c4b1] pl-6">
              {article.intro}
            </p>

            <div className="mt-10 space-y-12">
              {article.sections.map((s, i) => (
                <section key={i}>
                  <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-[#0d2731]">{s.h2}</h2>
                  <div className="mt-4 space-y-4">
                    {s.paras.map((p, j) => (
                      <p key={j} className="text-base sm:text-lg text-[#4b585e] leading-relaxed">{p}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* CTA contact */}
            <div data-testid="article-cta" className="mt-14 rounded-3xl bg-[#0d2731] p-8 sm:p-10">
              <p className="text-lg sm:text-xl font-extrabold tracking-tight text-[#f6f3ec] leading-snug">
                {article.conclusion}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3.5">
                <PhoneCTA location="article" className="!bg-[#f6f3ec] !text-[#0d2731] hover:!bg-[#b5c4b1]" />
                <WhatsAppCTA location="article" />
              </div>
              <Link
                to="/#simulateur"
                data-testid="article-cta-simulator"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#b5c4b1] hover:text-[#f6f3ec] transition-colors"
              >
                Simuler mes frais
                <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
              </Link>
            </div>

            {related.length > 0 && (
              <div className="mt-14">
                <p className="eyebrow"><span className="dot-sage" />À lire ensuite</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {related.map((r) => (
                    <ArticleCard key={r.slug} article={r} testId={`related-article-${r.slug}`} />
                  ))}
                </div>
              </div>
            )}

            <Link
              to="/conseils"
              data-testid="article-back"
              className="mt-12 inline-flex items-center gap-2 text-sm font-bold text-[#176b70] hover:text-[#0d2731] transition-colors"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
              Tous les conseils
            </Link>
          </Reveal>
        </div>
      </article>

      <ContactBlock />
    </>
  );
};

export default Article;
